import { API_BASE_URL } from './Constants';
import { authenticatedFetch } from './ApiInterceptor';
import { log, error } from '../utils/logger';

/**
 * Fetches the player's profile data from the backend.
 * @param {string} playerId - The ID of the player.
 * @returns {Promise<Object>} - The player's profile data.
 */
export const fetchPlayerProfile = async (playerId) => {
  try {
    if (!playerId) {
      throw new Error("Player ID is missing");
    }

    log(`Attempting to fetch profile for player ID: ${playerId}`);
    
    // Use authenticatedFetch instead of direct fetch
    const response = await authenticatedFetch(`/playerapi/players/${playerId}/`);

    log(`Response status: ${response.status} ${response.statusText}`);
    log(`Response type: ${response.headers.get('content-type')}`);
    
    // Check if response is not JSON (like HTML)
    const contentType = response.headers.get('content-type');
    if (contentType && !contentType.includes('application/json')) {
      // Get the full text response for debugging
      const textResponse = await response.text();
      error("Received non-JSON response:", textResponse.substring(0, 500) + '...');
      throw new Error(`Received non-JSON response: ${response.status} ${response.statusText}`);
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error response:", errorText);
      throw new Error(`Failed to fetch player profile: ${response.statusText}`);
    }

    const data = await response.json();
    log("Successfully fetched player profile data:", data);
    
    // Store game_ids in localStorage
    if (data.game_ids) {
      localStorage.setItem("game_ids", JSON.stringify(data.game_ids));
    }

    // API now directly returns the player data (not nested in a 'player' property)
    // We now return the data as-is instead of nesting it
    return {
      player: data  // Keep using player key for backward compatibility
    };
  } catch (err) {
    error("Error fetching player profile:", err.message);
    throw err;
  }
};

/**
 * Claims a daily spinner reward for the player
 * @returns {Promise<Object>} The reward information
 */
export const claimSpinnerReward = async () => {
  try {
    const response = await authenticatedFetch('/playerapi/spinner-reward/', {
      method: 'POST',
      body: JSON.stringify({})
    });

    if (!response.ok) {
      const errorData = await response.text();
      const errorMsg = errorData.includes('Endpoint under cooldown') 
        ? 'Spinner is on cooldown. Please try again later.'
        : `Failed to claim spinner reward: ${response.status} ${response.statusText}`;
      throw new Error(errorMsg);
    }

    const data = await response.json();
    //console.log("Successfully claimed spinner reward:", data);
    
    // Store cooldown information in localStorage
    if (data.cooldown && data.cooldown.expires_at) {
      localStorage.setItem('spinner_cooldown_expires', data.cooldown.expires_at);
    }
    
    return data;
  } catch (error) {
    console.error("Error claiming spinner reward:", error);
    throw error;
  }
};

/**
 * Checks if the spinner is on cooldown
 * @returns {Object} Cooldown status information
 */
export const checkSpinnerCooldown = () => {
  const expiryTime = localStorage.getItem('spinner_cooldown_expires');
  if (!expiryTime) {
    return { active: false, timeLeft: 0 };
  }
  
  const now = new Date();
  const expires = new Date(expiryTime);
  
  if (now >= expires) {
    // Cooldown has expired, clear it
    localStorage.removeItem('spinner_cooldown_expires');
    return { active: false, timeLeft: 0 };
  }
  
  // Calculate time left in seconds
  const timeLeftMs = expires - now;
  const timeLeftSeconds = Math.ceil(timeLeftMs / 1000);
  
  return {
    active: true,
    timeLeft: timeLeftSeconds,
    expiresAt: expiryTime
  };
};

/**
 * Claims a tournament reward for the player
 * @param {string} tournamentId - The ID of the tournament
 * @returns {Promise<Object>} The reward information
 */
export const claimTournamentReward = async (tournamentId) => {
	try {
		const response = await authenticatedFetch('/playerapi/claim-tournament-reward/', {
			method: 'POST',
			body: JSON.stringify({ tournament_id: tournamentId }),
		});

		if (!response.ok) {
			const errorText = await response.text();
			throw new Error(`Failed to claim tournament reward: ${response.status} ${response.statusText} - ${errorText}`);
		}

		const data = await response.json();
		return data;
	} catch (error) {
		console.error(`Error claiming tournament reward for ${tournamentId}:`, error);
		throw error;
	}
};

/**
 * Fetches all teams the player is a member of
 * @returns {Promise<Object>} Teams data
 */
export const fetchPlayerTeams = async () => {
  try {
    const response = await authenticatedFetch('/playerapi/my-teams/');

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch player teams: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching player teams:", error);
    throw error;
  }
};